'use client'

import { useState } from 'react'
import { supabase } from '../_utils/supabase'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Link from 'next/link'
import Logo from '../_components/Logo'
import FieldToastIndicator from '../_components/_ui/field-toast-indicator'

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const inputClasses = `
        w-full px-4 py-2 rounded-md
        border border-gray-300
        focus:border-blue-500 focus:ring-2 focus:ring-blue-200
        bg-white/10 backdrop-blur-sm
        text-gray-100
        placeholder-gray-400
    `

    const isEmailValid = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError('')
        setSuccessMessage('')

        if (!isEmailValid()) {
            setError('Please enter a valid email address')
            setLoading(false)
            return
        }

        try {
            const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            })

            if (resetError) {
                setError(getErrorMessage(resetError))
                return
            }

            setSuccessMessage('Password reset instructions have been sent to your email.')
            setEmail('') // Clear the form
        } catch (error) {
            console.error('Unexpected error:', error)
            setError('An unexpected error occurred')
        } finally {
            setLoading(false)
        }
    }

    const getErrorMessage = (error) => {
        if (error.message.includes('Email not found')) {
            return 'No account found with this email address.'
        } else if (error.message.includes('Rate limit exceeded')) {
            return 'Too many reset attempts. Please try again later.'
        } else {
            return error.message
        }
    }

    return (
        <div className="min-h-screen w-full relative flex items-center justify-center md:justify-end px-4">
            <BackgroundBeams className="opacity-40" />
            
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md p-8 mx-auto md:mx-12 my-10 
                         bg-white/10 backdrop-blur-lg rounded-2xl
                         border border-white/20 shadow-xl"
            >
                <Link href="/" className="block mb-8">
                    <Logo className="mx-auto" />
                </Link>

                <h1 className="text-2xl font-bold text-center text-white mb-4">
                    Reset Password
                </h1>
                
                <p className="text-gray-300 text-center mb-8">
                    Enter your email address and we'll send you instructions to reset your password.
                </p>

                <form onSubmit={handleResetPassword} className="space-y-6">
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-100 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputClasses}
                            required
                        />
                        {email && (
                            <FieldToastIndicator
                                visible={true}
                                message={isEmailValid() ? "Valid email format" : "Invalid email format"}
                                backgroundColor={isEmailValid() ? "#059669" : "#374151"}
                            />
                        )}
                    </div>

                    {error && (
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-red-400 text-sm text-center"
                        >
                            {error}
                        </motion.p>
                    )}

                    {successMessage && (
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-green-400 text-sm text-center"
                        >
                            {successMessage}
                        </motion.p>
                    )}

                    <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 px-4 rounded-lg 
                            ${loading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-blue-500 to-teal-400 cursor-pointer'}
                            text-white font-medium shadow-lg
                            transition-all duration-200`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sending...
                            </span>
                        ) : (
                            'Send Reset Instructions'
                        )}
                    </motion.button>

                    <div className="text-center">
                        <Link href="/login" className="text-sm text-gray-400 hover:text-gray-300">
                            Back to Login
                        </Link>
                    </div>
                </form>
            </motion.div>
        </div>
    )
} 